import { useState, useCallback, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Loader2, Lock, AlertTriangle, Sparkles } from "lucide-react";
import { Turnstile } from "@marsidev/react-turnstile";

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY || '';
const SESSION_KEY = 'studio-auth-token';

function StudioPasswordGate({ children }) {
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [authenticated, setAuthenticated] = useState(false);
  const [checking, setChecking] = useState(true);
  const [turnstileToken, setTurnstileToken] = useState(null);
  const [turnstileKey, setTurnstileKey] = useState(0);
  const [brandingLoaded, setBrandingLoaded] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setBrandingLoaded(true), 100);
    return () => clearTimeout(timer);
  }, []);

  // Check existing token on mount
  useEffect(() => {
    const token = sessionStorage.getItem(SESSION_KEY);
    if (!token) {
      setChecking(false);
      return;
    }
    fetch(`${API_URL}/api/studio/verify?token=${encodeURIComponent(token)}`)
      .then(r => r.json())
      .then(data => {
        if (data.valid) {
          setAuthenticated(true);
        } else {
          sessionStorage.removeItem(SESSION_KEY);
        }
      })
      .catch(() => {
        sessionStorage.removeItem(SESSION_KEY);
      })
      .finally(() => setChecking(false));
  }, []);

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    if (!password.trim()) return;

    setIsLoading(true);
    setError(null);

    try {
      const response = await fetch(`${API_URL}/api/studio/auth`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          password: password.trim(),
          turnstile_token: turnstileToken,
        }),
      });

      let data;
      try {
        const text = await response.text();
        data = JSON.parse(text);
      } catch {
        throw new Error('Invalid server response');
      }

      if (!data.success) {
        setError(data.error || 'Authentication failed');
        setTurnstileKey(prev => prev + 1);
        setTurnstileToken(null);
        return;
      }

      sessionStorage.setItem(SESSION_KEY, data.token);
      setAuthenticated(true);
    } catch (err) {
      setError(err.message || 'Connection error');
      setTurnstileKey(prev => prev + 1);
      setTurnstileToken(null);
    } finally {
      setIsLoading(false);
    }
  }, [password, turnstileToken]);

  // Loading state while checking existing token
  if (checking) {
    return (
      <div data-testid="studio-gate-loading" className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }

  // Authenticated — render children
  if (authenticated) {
    return children;
  }

  // Password gate
  return (
    <div data-testid="studio-password-gate" className="min-h-screen flex items-center justify-center py-8 px-4">
      <div className="w-full max-w-sm space-y-6">
        {/* Branding */}
        <div className="text-center space-y-2">
          <div
            className={`inline-flex items-center gap-1.5 text-xs text-muted-foreground transition-all duration-500 ${
              brandingLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'
            }`}
          >
            <Sparkles className="h-3 w-3" />
            Sovereign Studio
          </div>
          <h1
            className={`text-2xl sm:text-3xl font-bold tracking-tight transition-all duration-500 delay-100 ${
              brandingLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'
            }`}
          >
            Sovereign Studio
          </h1>
          <p
            className={`text-muted-foreground text-sm transition-all duration-500 delay-200 ${
              brandingLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'
            }`}
          >
            Enter the password to continue
          </p>
        </div>

        {/* Login Card */}
        <Card className="border border-border shadow-md animate-entrance">
          <CardHeader className="animate-entrance-delay-1">
            <div className="flex items-center gap-2">
              <Lock className="h-5 w-5 text-muted-foreground" />
              <CardTitle>Access Required</CardTitle>
            </div>
            <CardDescription>
              This studio is password-protected for authorized users.
            </CardDescription>
          </CardHeader>
          <CardContent className="animate-entrance-delay-2">
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-1.5">
                <label htmlFor="studio-password" className="text-sm font-medium">
                  Password
                </label>
                <input
                  data-testid="studio-password-input"
                  id="studio-password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter password"
                  disabled={isLoading}
                  autoFocus
                  className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                />
              </div>

              {TURNSTILE_SITE_KEY && (
                <div data-testid="studio-gate-turnstile" className="flex justify-center">
                  <Turnstile
                    key={turnstileKey}
                    siteKey={TURNSTILE_SITE_KEY}
                    onSuccess={setTurnstileToken}
                    onError={() => setTurnstileToken(null)}
                    onExpire={() => setTurnstileToken(null)}
                  />
                </div>
              )}

              {error && (
                <Alert variant="destructive" data-testid="studio-gate-error">
                  <AlertTriangle className="h-4 w-4" />
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              <Button
                data-testid="studio-gate-submit"
                type="submit"
                className="w-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
                disabled={
                  isLoading ||
                  !password.trim() ||
                  (TURNSTILE_SITE_KEY && !turnstileToken)
                }
              >
                {isLoading ? (
                  <>
                    <Loader2 className="animate-spin" />
                    <span>Verifying...</span>
                  </>
                ) : (
                  <>
                    <Lock className="h-4 w-4" />
                    <span>Unlock Studio</span>
                  </>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Footer */}
        <div className="text-center text-xs text-muted-foreground">
          Powered by DMI &middot; Privacy by Architecture
        </div>
      </div>
    </div>
  );
}

export default StudioPasswordGate;
